import { CalendarDays, ChevronLeft, ChevronRight, Download, Eye, FileText, Image as ImageIcon, Mail, MapPin, Phone, Search, Video, X } from 'lucide-react'
import { FormEvent, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import mammoth from 'mammoth'
import * as XLSX from 'xlsx'
import { PageHero, SectionTitle } from '../components/Common'
import { SchoolMap } from '../components/SchoolMap'
import { api } from '../services/api'
import type { SchoolInfo } from '../types/content'

// Fallback school info
const defaultSchool: SchoolInfo = {
  name: 'TRƯỜNG THCS LÝ THƯỜNG KIỆT',
  address: 'Quảng Phú, Đà Nẵng, Việt Nam',
  phone: '0235 3821 234',
  email: 'lythuongket@edu.vn',
  website: 'https://thcslythuongkietquangphu-danang.edu.vn',
  workingHours: 'Thứ 2 - Thứ 6 (07:00 - 17:00)',
  mapLatitude: 15.5737,
  mapLongitude: 108.4742,
  mapZoom: 16,
}

const fileURL = (url?: string) => {
  if (!url) return '#'
  if (/^https?:\/\//i.test(url)) return url
  return `${import.meta.env.VITE_CMS_URL || 'http://localhost:3001'}${url}`
}

const documentPreviewURL = (doc: {
  title: string
  file?: { url?: string; filename?: string; mimeType?: string }
}) => {
  const url = fileURL(doc.file?.url)
  if (url === '#') return '#'
  const params = new URLSearchParams({
    url,
    title: doc.title,
    filename: doc.file?.filename ?? doc.title,
    mimeType: doc.file?.mimeType ?? '',
  })
  return `/xem-van-ban?${params.toString()}`
}

const getPreviewKind = (mimeType: string, filename: string) => {
  const lowerName = filename.toLowerCase()
  const lowerMime = mimeType.toLowerCase()
  if (lowerMime.includes('pdf') || lowerName.endsWith('.pdf')) return 'pdf'
  if (lowerMime.includes('word') || lowerName.endsWith('.docx')) return 'docx'
  if (lowerMime.includes('spreadsheet') || lowerMime.includes('excel') || lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls')) return 'sheet'
  return 'unsupported'
}

export function AnnouncementsPage() {
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useQuery({
    queryKey: ['announcements', page],
    queryFn: () => api.getAnnouncements(page, 20),
  })

  const items = data?.data ?? []
  const meta = data?.meta
  const totalPages = meta?.totalPages ?? 1

  return (
    <>
      <PageHero title="THÔNG BÁO" />
      <div className="container generic-page">
        <SectionTitle>THÔNG BÁO MỚI NHẤT</SectionTitle>
        {isLoading && <p>Đang tải thông báo...</p>}
        {isError && <p>Không thể tải dữ liệu thông báo.</p>}
        {!isLoading && !isError && items.length === 0 && <p>Chưa có thông báo nào.</p>}

        <div className="announcement-list">
          {items.map((item) => (
            <Link to={`/thong-bao/${item.slug || item.id}`} state={{ from: '/thong-bao', fromLabel: 'Danh sách thông báo' }} key={item.id}>
              <article>
                <CalendarDays />
                <div>
                  <h3>{item.title}</h3>
                  <span>{item.publishedAt ? new Date(item.publishedAt).toLocaleDateString('vi-VN') : item.date}</span>
                  <p>{item.summary || 'Thông tin chi tiết được nhà trường cập nhật và công khai đến phụ huynh, học sinh và cán bộ giáo viên.'}</p>
                </div>
                {item.priority === 'urgent' || item.important ? <b>Quan trọng</b> : null}
              </article>
            </Link>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="pagination" style={{ marginTop: '25px' }}>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button key={p} className={page === p ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

export function AnnouncementDetailPage() {
  const { slug } = useParams()
  const location = useLocation()
  const state = location.state as { from?: string; fromLabel?: string } | null
  const backTo = state?.from || '/thong-bao'
  const backLabel = state?.fromLabel || 'Danh sách thông báo'
  const { data: item, isLoading, isError } = useQuery({
    queryKey: ['announcement', slug],
    queryFn: () => api.getAnnouncement(slug || ''),
    enabled: Boolean(slug),
  })

  if (isLoading) return (
    <>
      <PageHero title="THÔNG BÁO" />
      <div className="container generic-page"><p>Đang tải thông báo...</p></div>
    </>
  )

  if (isError || !item) return (
    <>
      <PageHero title="THÔNG BÁO" />
      <div className="container generic-page">
        <Link className="back-link" to={backTo}>← Quay về {backLabel}</Link>
        <p>Không tìm thấy thông báo.</p>
      </div>
    </>
  )

  const publishedDate = item.publishedAt ? new Date(item.publishedAt).toLocaleDateString('vi-VN') : item.date

  return (
    <>
      <div className="container breadcrumb-line">
        <Link to="/">Trang chủ</Link><span>›</span>
        <Link to="/thong-bao">Thông báo</Link><span>›</span>
        <span>Chi tiết</span>
      </div>
      <div className="container generic-page">
        <article className="announcement-detail">
          <Link className="back-link" to={backTo}>← Quay về {backLabel}</Link>
          <div className="announcement-detail__meta">
            <CalendarDays />
            <span>{publishedDate}</span>
            {item.priority === 'urgent' || item.important ? <b>Quan trọng</b> : null}
          </div>
          <h1>{item.title}</h1>
          {item.summary && <p className="announcement-detail__summary">{item.summary}</p>}
          <div className="article-body">
            {item.content && typeof item.content === 'object' ? (
              <SimpleRichText content={item.content} />
            ) : (
              <p>{item.summary || 'Nội dung thông báo đang được cập nhật.'}</p>
            )}
          </div>
          {item.attachments && item.attachments.length > 0 && (
            <div className="attachment">
              <h3><FileText />Tệp đính kèm</h3>
              {item.attachments.map((att) => (
                <a href={fileURL(att.url)} key={att.id} download target="_blank" rel="noreferrer">
                  <span className="file-icon">{att.mimeType?.includes('pdf') ? 'PDF' : 'FILE'}</span>
                  <div>
                    <strong>{att.filename || 'Tải xuống'}</strong>
                    {att.filesize && <small>{(att.filesize / 1024).toFixed(0)} KB</small>}
                  </div>
                  <Download />
                </a>
              ))}
            </div>
          )}
        </article>
      </div>
    </>
  )
}

function SimpleRichText({ content }: { content: unknown }) {
  const data = content as { root?: { children?: unknown[] } }
  if (!data?.root?.children) return null
  return <div className="rich-text">{data.root.children.map((node: any, index) => renderRichNode(node, index))}</div>
}

function renderRichNode(node: any, key: number) {
  if (!node) return null
  if (node.type === 'paragraph') return <p key={key}>{node.children?.map((child: any, index: number) => renderRichLeaf(child, index))}</p>
  if (node.type === 'heading') {
    const Tag = `h${node.tag?.replace('h', '') || '2'}` as 'h1' | 'h2' | 'h3' | 'h4'
    return <Tag key={key}>{node.children?.map((child: any, index: number) => renderRichLeaf(child, index))}</Tag>
  }
  if (node.type === 'list') {
    return node.listType === 'number'
      ? <ol key={key}>{node.children?.map((child: any, index: number) => renderRichNode(child, index))}</ol>
      : <ul key={key}>{node.children?.map((child: any, index: number) => renderRichNode(child, index))}</ul>
  }
  if (node.type === 'listitem') return <li key={key}>{node.children?.map((child: any, index: number) => renderRichLeaf(child, index))}</li>
  if (node.type === 'quote') return <blockquote key={key}>{node.children?.map((child: any, index: number) => renderRichNode(child, index))}</blockquote>
  return null
}

function renderRichLeaf(leaf: any, key: number) {
  if (!leaf || leaf.type !== 'text') return null
  let el: any = leaf.text
  if (leaf.format & 1) el = <strong>{el}</strong>
  if (leaf.format & 2) el = <em>{el}</em>
  if (leaf.format & 8) el = <u>{el}</u>
  return <span key={key}>{el}</span>
}

export function DocumentsPage() {
  const [term, setTerm] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['documents', page],
    queryFn: () => api.getDocuments(page, 20),
  })

  const documents = data?.data ?? []

  // Filter documents client-side based on user search term
  const items = useMemo(() => {
    return documents.filter((d) => d.title.toLowerCase().includes(term.toLowerCase()))
  }, [documents, term])

  return (
    <>
      <PageHero title="VĂN BẢN - CÔNG KHAI" />
      <div className="container generic-page">
        <div className="document-toolbar">
          <div>
            <Search />
            <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Tìm kiếm văn bản..." />
          </div>
          <select>
            <option>Tất cả loại văn bản</option>
            <option>Kế hoạch</option>
            <option>Quyết định</option>
            <option>Thông báo</option>
          </select>
        </div>

        <div className="document-table">
          <div className="document-table__head">
            <span>Mã văn bản</span>
            <span>Tên văn bản</span>
            <span>Ngày ban hành</span>
            <span>Tệp</span>
          </div>

          {isLoading && <div style={{ padding: '20px', textAlign: 'center' }}>Đang tải văn bản...</div>}
          {isError && <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>Lỗi tải danh sách văn bản.</div>}
          {!isLoading && !isError && items.length === 0 && (
            <div style={{ padding: '20px', textAlign: 'center' }}>Không tìm thấy văn bản phù hợp.</div>
          )}

          {items.map((doc) => {
            const docCode = doc.code || doc.documentCode || 'VB-CN-2024'
            const docUrl = fileURL(doc.file?.url)
            const previewUrl = documentPreviewURL(doc)
            const docSize = doc.file?.filesize ? `${(doc.file.filesize / 1024).toFixed(0)} KB` : doc.size || 'PDF'
            const canOpen = docUrl !== '#'

            return (
              <div className="document-table__row" key={doc.id}>
                <strong>{docCode}</strong>
                <div>
                  <FileText />
                  <span>{doc.title}<small>{docSize}</small></span>
                </div>
                <span>{doc.issuedAt ? new Date(doc.issuedAt).toLocaleDateString('vi-VN') : ''}</span>
                <div className="document-actions">
                  <a href={previewUrl} target="_blank" rel="noreferrer" aria-disabled={!canOpen} title="Xem văn bản">
                    <button disabled={!canOpen} aria-label="Xem văn bản"><Eye /></button>
                  </a>
                  <a href={docUrl} download target="_blank" rel="noreferrer" aria-disabled={!canOpen} title="Tải xuống">
                    <button disabled={!canOpen} aria-label="Tải xuống"><Download /></button>
                  </a>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

export function DocumentPreviewPage() {
  const [params] = useSearchParams()
  const url = params.get('url') ?? ''
  const title = params.get('title') ?? 'Văn bản'
  const filename = params.get('filename') ?? title
  const mimeType = params.get('mimeType') ?? ''
  const kind = getPreviewKind(mimeType, filename)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['document-preview', url, kind],
    enabled: Boolean(url) && kind !== 'pdf',
    queryFn: async () => {
      const response = await fetch(url)
      if (!response.ok) throw new Error(`Không thể tải tệp (${response.status})`)
      const arrayBuffer = await response.arrayBuffer()

      if (kind === 'docx') {
        const result = await mammoth.convertToHtml({ arrayBuffer })
        return { html: result.value, type: 'docx' as const }
      }

      if (kind === 'sheet') {
        const workbook = XLSX.read(arrayBuffer, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        const sheet = sheetName ? workbook.Sheets[sheetName] : undefined
        return {
          html: sheet ? XLSX.utils.sheet_to_html(sheet, { id: 'document-preview-sheet' }) : '<p>Tệp Excel không có dữ liệu.</p>',
          type: 'sheet' as const,
        }
      }

      return { html: '<p>Định dạng này chưa hỗ trợ xem trực tiếp.</p>', type: 'unsupported' as const }
    },
  })

  return (
    <>
      <PageHero title="XEM VĂN BẢN" />
      <div className="container document-preview-page">
        <div className="document-preview-toolbar">
          <div>
            <FileText />
            <span>{title}<small>{filename}</small></span>
          </div>
          {url && (
            <a href={url} download target="_blank" rel="noreferrer" title="Tải xuống">
              <button aria-label="Tải xuống"><Download /></button>
            </a>
          )}
        </div>

        {!url && <p>Không tìm thấy đường dẫn tệp.</p>}
        {url && kind === 'pdf' && <iframe className="document-preview-frame" src={url} title={title} />}
        {url && kind !== 'pdf' && isLoading && <p>Đang mở văn bản...</p>}
        {url && kind !== 'pdf' && isError && <p>{error instanceof Error ? error.message : 'Không thể mở văn bản.'}</p>}
        {url && kind !== 'pdf' && data && (
          <div
            className={`document-preview-content document-preview-content--${data.type}`}
            dangerouslySetInnerHTML={{ __html: data.html }}
          />
        )}
      </div>
    </>
  )
}

export function LibraryPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [viewerAlbum, setViewerAlbum] = useState<typeof albums[number] | null>(null)
  const [viewerIndex, setViewerIndex] = useState(0)
  const tabParam = searchParams.get('tab')
  const activeTab: 'images' | 'videos' | 'resources' =
    location.pathname.endsWith('/video') || tabParam === 'video'
      ? 'videos'
      : location.pathname.endsWith('/tai-lieu-hoc-tap') || tabParam === 'tai-lieu-hoc-tap'
        ? 'resources'
        : 'images'

  const selectTab = (tab: 'images' | 'videos' | 'resources') => {
    if (tab === 'images') navigate('/thu-vien/hinh-anh')
    if (tab === 'videos') navigate('/thu-vien/video')
    if (tab === 'resources') navigate('/thu-vien/tai-lieu-hoc-tap')
  }

  const { data: albums = [], isLoading: isAlbumsLoading } = useQuery({
    queryKey: ['library-albums'],
    queryFn: () => api.getAlbums(),
  })

  const { data: resourcesData, isLoading: isResourcesLoading } = useQuery({
    queryKey: ['library-resources'],
    queryFn: () => api.getLearningResources(1, 50),
  })

  const resources = resourcesData?.data ?? []
  const visibleAlbums = albums.filter((album) => {
    const albumType = album.albumType ?? 'image'
    if (activeTab === 'images') return albumType === 'image' || albumType === 'mixed'
    if (activeTab === 'videos') return albumType === 'video' || albumType === 'mixed'
    return false
  })

  const resourceMeta = (item: typeof resources[number]) => {
    const parts = [
      typeof item.subject === 'object' ? item.subject?.name || item.subject?.title : undefined,
      typeof item.gradeLevel === 'object' ? item.gradeLevel?.name || item.gradeLevel?.title : undefined,
      item.schoolYear,
    ].filter(Boolean)
    return parts.join(' - ') || 'Tài liệu học tập'
  }

  const openAlbum = (album: typeof albums[number]) => {
    setViewerAlbum(album)
    setViewerIndex(0)
  }

  const closeAlbum = () => {
    setViewerAlbum(null)
    setViewerIndex(0)
  }

  const viewerItems = viewerAlbum
    ? (viewerAlbum.items?.length ? viewerAlbum.items : viewerAlbum.cover ? [viewerAlbum.cover] : [])
    : []
  const currentViewerItem = viewerItems[viewerIndex]
  const currentViewerURL = fileURL(currentViewerItem?.url)
  const currentIsVideo = currentViewerItem?.mimeType?.startsWith('video/') || viewerAlbum?.albumType === 'video'
  const canStepViewer = viewerItems.length > 1
  const stepViewer = (direction: -1 | 1) => {
    if (!viewerItems.length) return
    setViewerIndex((index) => (index + direction + viewerItems.length) % viewerItems.length)
  }

  return (
    <>
      <PageHero title="THƯ VIỆN" />
      <div className="container generic-page">
        <div className="library-tabs">
          <button type="button" className={activeTab === 'images' ? 'active' : ''} onClick={() => selectTab('images')}><ImageIcon />Hình ảnh</button>
          <button type="button" className={activeTab === 'videos' ? 'active' : ''} onClick={() => selectTab('videos')}><Video />Video</button>
          <button type="button" className={activeTab === 'resources' ? 'active' : ''} onClick={() => selectTab('resources')}><FileText />Tài liệu học tập</button>
        </div>

        {activeTab !== 'resources' && isAlbumsLoading && <p>Đang tải thư viện...</p>}
        {activeTab === 'resources' && isResourcesLoading && <p>Đang tải tài liệu học tập...</p>}

        {activeTab !== 'resources' && !isAlbumsLoading && visibleAlbums.length === 0 && (
          <p>Chưa có nội dung cho chuyên mục này.</p>
        )}

        {activeTab !== 'resources' && (
          <div className="library-grid">
            {visibleAlbums.map((item, index) => (
              <article className="library-album-card" key={item.id || index} onClick={() => openAlbum(item)} role="button" tabIndex={0} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') openAlbum(item) }}>
                <img src={item.cover?.url ? fileURL(item.cover.url) : '/assets/images/activity-art.jpg'} alt={item.title} />
                <div>
                  <span>{activeTab === 'videos' ? <Video /> : <ImageIcon />}</span>
                  <h3>{item.title}</h3>
                  <p>{item.description || (activeTab === 'videos' ? 'Video hoạt động nổi bật của nhà trường.' : 'Album hoạt động nổi bật của nhà trường.')}</p>
                </div>
              </article>
            ))}
          </div>
        )}

        {viewerAlbum && (
          <div className="library-viewer" role="dialog" aria-modal="true" aria-label={viewerAlbum.title}>
            <div className="library-viewer__panel">
              <div className="library-viewer__header">
                <div>
                  <h2>{viewerAlbum.title}</h2>
                  <p>{viewerAlbum.description || `${viewerIndex + 1}/${Math.max(viewerItems.length, 1)}`}</p>
                </div>
                <button type="button" onClick={closeAlbum} aria-label="Đóng"><X /></button>
              </div>

              <div className="library-viewer__stage">
                {canStepViewer && (
                  <button type="button" className="library-viewer__nav library-viewer__nav--prev" onClick={() => stepViewer(-1)} aria-label="Trước"><ChevronLeft /></button>
                )}
                {currentViewerURL !== '#' && currentIsVideo ? (
                  <video src={currentViewerURL} controls autoPlay />
                ) : currentViewerURL !== '#' ? (
                  <img src={currentViewerURL} alt={currentViewerItem?.alt || viewerAlbum.title} />
                ) : (
                  <p>Không tìm thấy media.</p>
                )}
                {canStepViewer && (
                  <button type="button" className="library-viewer__nav library-viewer__nav--next" onClick={() => stepViewer(1)} aria-label="Tiếp"><ChevronRight /></button>
                )}
              </div>

              {viewerItems.length > 1 && (
                <div className="library-viewer__thumbs">
                  {viewerItems.map((media, index) => (
                    <button type="button" className={index === viewerIndex ? 'active' : ''} key={`${media.url || 'media'}-${index}`} onClick={() => setViewerIndex(index)}>
                      {media.mimeType?.startsWith('video/') ? <Video /> : <img src={fileURL(media.url)} alt={media.alt || `${viewerAlbum.title} ${index + 1}`} />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'resources' && !isResourcesLoading && resources.length === 0 && (
          <p>Chưa có tài liệu học tập.</p>
        )}

        {activeTab === 'resources' && (
          <div className="library-grid">
            {resources.map((item, index) => {
              const href = item.videoUrl || documentPreviewURL(item)
              const hasLink = href && href !== '#'
              return (
                <article className="library-resource-card" key={item.id || index}>
                  <div>
                    <span>{item.resourceType === 'video' ? <Video /> : <FileText />}</span>
                    <h3>{item.title}</h3>
                    <p>{item.description || resourceMeta(item)}</p>
                    {hasLink && (
                      item.videoUrl ? (
                        <a href={href} target="_blank" rel="noreferrer">Xem video</a>
                      ) : (
                        <Link to={href}>Xem tài liệu</Link>
                      )
                    )}
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}

export function AdmissionsPage() {
  const { data } = useQuery({
    queryKey: ['admissions-docs'],
    queryFn: () => api.getDocuments(1, 5),
  })

  const docs = data?.data ?? []

  return (
    <>
      <PageHero title="TUYỂN SINH" />
      <div className="container generic-page admissions-page">
        <section className="admission-hero">
          <div>
            <span>NĂM HỌC 2025 - 2026</span>
            <h2>TUYỂN SINH VÀO LỚP 6</h2>
            <p>Trường THCS Nguyễn Văn Linh thông báo kế hoạch tuyển sinh lớp 6, tiếp nhận hồ sơ đúng tuyến và tạo điều kiện thuận lợi cho phụ huynh, học sinh.</p>
            <button>Đăng ký tư vấn</button>
          </div>
          <img src="/assets/images/school-building.jpg" alt="Tuyển sinh" />
        </section>

        <div className="admission-steps">
          {[
            ['01', 'Đối tượng tuyển sinh', 'Học sinh hoàn thành chương trình tiểu học, thuộc địa bàn tuyển sinh.'],
            ['02', 'Thời gian nhận hồ sơ', 'Từ ngày 01/07/2025 đến ngày 15/07/2025.'],
            ['03', 'Hồ sơ cần chuẩn bị', 'Phiếu đăng ký, học bạ tiểu học, bản sao giấy khai sinh và giấy tờ cư trú.'],
            ['04', 'Công bố kết quả', 'Danh sách trúng tuyển được công bố trên website và bảng tin nhà trường.'],
          ].map(([step, title, desc]) => (
            <article key={step}>
              <strong>{step}</strong>
              <h3>{title}</h3>
              <p>{desc}</p>
            </article>
          ))}
        </div>

        <section>
          <SectionTitle>HỒ SƠ - BIỂU MẪU TUYỂN SINH</SectionTitle>
          {docs.length > 0 ? (
            docs.slice(0, 3).map((doc) => {
              const docUrl = fileURL(doc.file?.url)
              const docSize = doc.file?.filesize ? `${(doc.file.filesize / 1024).toFixed(0)} KB` : doc.size || 'PDF'

              return (
                <a className="download-card" href={docUrl} key={doc.id} download target="_blank" rel="noreferrer">
                  <FileText />
                  <div>
                    <strong>{doc.title}</strong>
                    <span>{doc.type || 'PDF'} • {docSize}</span>
                  </div>
                  <Download />
                </a>
              )
            })
          ) : (
            <p style={{ fontSize: '11px', color: '#8a91a1' }}>Đang cập nhật biểu mẫu tuyển sinh.</p>
          )}
        </section>
      </div>
    </>
  )
}

export function ContactPage() {
  const [status, setStatus] = useState('')
  const { data: configData } = useQuery({
    queryKey: ['site-config'],
    queryFn: () => api.getSiteConfig(),
  })

  const school = configData?.site ?? defaultSchool
  const schoolAddress = school.address ?? defaultSchool.address
  const schoolPhone = school.phone ?? defaultSchool.phone
  const schoolEmail = school.email ?? defaultSchool.email
  const schoolWebsite = school.website ?? defaultSchool.website
  const workingHours = school.workingHours ?? defaultSchool.workingHours
  const mapLatitude = school.mapLatitude ?? defaultSchool.mapLatitude
  const mapLongitude = school.mapLongitude ?? defaultSchool.mapLongitude
  const mapZoom = school.mapZoom ?? defaultSchool.mapZoom

  const mutation = useMutation({
    mutationFn: api.submitContact,
    onSuccess: () => {
      setStatus('Nhà trường đã nhận được liên hệ của bạn. Chúng tôi sẽ phản hồi sớm nhất.')
    },
    onError: () => {
      setStatus('Gửi liên hệ thất bại. Vui lòng kiểm tra lại kết nối.')
    }
  })

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)

    mutation.mutate({
      fullName: String(fd.get('fullName')),
      email: String(fd.get('email')),
      phone: String(fd.get('phone')),
      message: String(fd.get('message')),
    })

    e.currentTarget.reset()
  }

  return (
    <>
      <PageHero title="LIÊN HỆ" />
      <div className="container contact-page">
        <div className="contact-card">
          <SectionTitle>THÔNG TIN NHÀ TRƯỜNG</SectionTitle>
          <p><MapPin /><span><strong>Địa chỉ</strong>{schoolAddress}</span></p>
          <p><Phone /><span><strong>Điện thoại</strong>{schoolPhone}</span></p>
          <p><Mail /><span><strong>Email</strong>{schoolEmail}</span></p>
          <p><CalendarDays /><span><strong>Giờ làm việc</strong>{workingHours}</span></p>
          <SchoolMap
            title={school.schoolName ?? school.name ?? defaultSchool.name}
            address={schoolAddress}
            latitude={mapLatitude}
            longitude={mapLongitude}
            zoom={mapZoom}
            className="school-map--contact"
          />
        </div>

        <form className="contact-form" onSubmit={submit}>
          <SectionTitle>GỬI LIÊN HỆ</SectionTitle>
          <div className="form-grid">
            <label>Họ và tên *<input name="fullName" required /></label>
            <label>Email *<input name="email" type="email" required /></label>
            <label>Số điện thoại<input name="phone" /></label>
            <label>Chủ đề
              <select name="subject">
                <option>Tư vấn chung</option>
                <option>Tuyển sinh</option>
                <option>Học tập</option>
                <option>Góp ý</option>
              </select>
            </label>
          </div>
          <label>Nội dung *<textarea name="message" required rows={7} /></label>
          <button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Đang gửi...' : 'Gửi liên hệ'}
          </button>
          {status && <p className="success-message" style={{ marginTop: '15px' }}>{status}</p>}
        </form>
      </div>
    </>
  )
}

export function SearchPage() {
  const [params] = useSearchParams()
  const q = params.get('q') || ''

  const { data: results = [], isLoading, isError } = useQuery({
    queryKey: ['search', q],
    queryFn: () => api.search(q),
    enabled: Boolean(q.trim()),
  })

  return (
    <>
      <PageHero title="TÌM KIẾM" />
      <div className="container generic-page search-results">
        <h2>Kết quả tìm kiếm cho: “{q}”</h2>
        {isLoading && <p>Đang tìm kiếm...</p>}
        {isError && <p>Lỗi thực hiện tìm kiếm.</p>}
        {!isLoading && !isError && results.length > 0 ? (
          results.map((item) => {
            const targetUrl = item.type === 'post' ? `/tin-tuc/${item.slug}` : `/${item.slug || ''}`
            return (
              <article key={item.id}>
                <img src={item.image || '/assets/images/news-opening.jpg'} alt="" />
                <div>
                  <span>{item.category || item.type.toUpperCase()}</span>
                  <h3><Link to={targetUrl}>{item.title}</Link></h3>
                  <p>{item.excerpt}</p>
                </div>
              </article>
            )
          })
        ) : (
          !isLoading && <p>Không tìm thấy nội dung phù hợp.</p>
        )}
      </div>
    </>
  )
}

export function SitemapPage() {
  return (
    <>
      <PageHero title="SITEMAP" />
      <div className="container generic-page sitemap">
        <a href="/">Trang chủ</a>
        <a href="/gioi-thieu">Giới thiệu</a>
        <a href="/gioi-thieu/ve-nha-truong">Chi tiết nhà trường</a>
        <a href="/tin-tuc">Tin tức - Sự kiện</a>
        <a href="/dao-tao">Đào tạo</a>
        <a href="/lich-cong-tac">Lịch công tác</a>
        <a href="/thong-bao">Thông báo</a>
        <a href="/van-ban">Văn bản</a>
        <a href="/thu-vien">Thư viện</a>
        <a href="/tuyen-sinh">Tuyển sinh</a>
        <a href="/lien-he">Liên hệ</a>
      </div>
    </>
  )
}

export function StaticPage() {
  const location = useLocation()
  const title = location.pathname.split('/').filter(Boolean).join(' ').toUpperCase() || 'THÔNG TIN'

  return (
    <>
      <PageHero title={title} />
      <div className="container generic-page">
        <SectionTitle>NỘI DUNG ĐANG ĐƯỢC CẬP NHẬT</SectionTitle>
        <p>Trang nội dung này được quản lý từ Payload CMS. Quản trị viên có thể tạo nội dung, sắp xếp khối và xuất bản mà không cần sửa mã nguồn.</p>
      </div>
    </>
  )
}
